import React from 'react';
import { Anchor } from 'lucide-react';

export interface CrewRecord {
  id: string;
  name: string;
  role: string;
  position: string;
  status: 'working' | 'resting' | 'standby';
  workType: string;
  cumulativeHours: number;
  boardedAt: string;
}

interface Props {
  vessel: { id: string; name: string; code: string } | null;
  currentTime: number;
  crew: CrewRecord[];
}

const STATUS_CFG = {
  working: { label: '勤務中', color: '#0EA5E9', bg: '#0EA5E9' },
  resting: { label: '休憩中', color: '#22C55E', bg: '#22C55E' },
  standby: { label: '待機中', color: '#64748B', bg: '#64748B' },
} as const;

const ROLE_LABEL: Record<string, string> = {
  captain:        '船長',
  chief_engineer: '機関長',
  first_officer:  '一等航海士',
  engineer:       '機関士',
  crew:           '乗組員',
  cook:           '司厨長',
  shore_admin:    '陸上管理',
  system_admin:   'システム管理',
};

const CrewStatusPanel: React.FC<Props> = ({ vessel, currentTime, crew }) => {
  const timeStr = new Date(currentTime).toLocaleString('ja-JP', {
    month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
  });

  const counts = { working: 0, resting: 0, standby: 0 };
  for (const c of crew) {
    if (c.status in counts) counts[c.status]++;
  }

  return (
    <div style={{
      width: 300, minWidth: 300,
      background: '#0F172A',
      display: 'flex', flexDirection: 'column',
      overflow: 'hidden',
    }}>
      {/* ヘッダー */}
      <div style={{ padding: '14px 16px', borderBottom: '1px solid #1E3A5F', flexShrink: 0 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: '#38BDF8', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 8 }}>
          乗務員ステータス
        </div>
        {vessel ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ color: '#E2E8F0', fontSize: 14, fontWeight: 700 }}>{vessel.name}</span>
            <span style={{ fontSize: 10, color: '#475569' }}>{timeStr}</span>
          </div>
        ) : (
          <div style={{ color: '#475569', fontSize: 13 }}>船舶を選択してください</div>
        )}
      </div>

      {/* ステータス集計バー */}
      {vessel && (
        <div style={{ padding: '10px 16px', borderBottom: '1px solid #1E3A5F', display: 'flex', gap: 4, flexShrink: 0 }}>
          {(Object.entries(STATUS_CFG) as [keyof typeof STATUS_CFG, typeof STATUS_CFG[keyof typeof STATUS_CFG]][]).map(([k, cfg]) => (
            <div key={k} style={{ flex: 1, textAlign: 'center', padding: '6px 0', borderRadius: 8, background: counts[k] > 0 ? cfg.bg + '18' : 'transparent' }}>
              <div style={{ fontSize: 20, fontWeight: 800, color: counts[k] > 0 ? cfg.color : '#334155' }}>
                {counts[k]}
              </div>
              <div style={{ fontSize: 9, color: '#475569', marginTop: 1, fontWeight: 600 }}>{cfg.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* 乗務員リスト */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '8px' }}>
        {!vessel && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 10, paddingTop: 48 }}>
            <Anchor size={28} color="#1E3A5F" />
            <div style={{ color: '#334155', fontSize: 13, textAlign: 'center', lineHeight: 1.6 }}>
              左の船舶タブから<br />船を選択してください
            </div>
          </div>
        )}
        {vessel && crew.length === 0 && (
          <div style={{ color: '#334155', fontSize: 13, textAlign: 'center', marginTop: 40, lineHeight: 1.8 }}>
            乗船中の乗務員がいません<br />
            <span style={{ fontSize: 11, color: '#1E3A5F' }}>乗組員配置管理で配属設定してください</span>
          </div>
        )}
        {crew.map(c => {
          const cfg = STATUS_CFG[c.status];
          const displayRole = c.position || ROLE_LABEL[c.role] || c.role;
          return (
            <div key={c.id} style={{
              background: 'rgba(255,255,255,0.03)',
              border: '1px solid #1E3A5F',
              borderRadius: 10, padding: '11px 13px', marginBottom: 7,
            }}>
              {/* 名前・役職・ステータス */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: c.workType ? 6 : 8 }}>
                <div>
                  <div style={{ color: '#E2E8F0', fontSize: 13, fontWeight: 700 }}>{c.name}</div>
                  <div style={{ color: '#475569', fontSize: 11, marginTop: 1 }}>{displayRole}</div>
                </div>
                <span style={{
                  padding: '2px 9px', borderRadius: 20, fontSize: 10, fontWeight: 700,
                  background: cfg.bg + '22', color: cfg.color,
                  border: `1px solid ${cfg.color}44`, flexShrink: 0,
                }}>{cfg.label}</span>
              </div>

              {/* 作業種別（勤務中のとき） */}
              {c.workType && (
                <div style={{
                  background: 'rgba(14,165,233,0.10)', borderRadius: 6,
                  padding: '4px 9px', marginBottom: 8,
                  fontSize: 12, color: '#38BDF8', fontWeight: 600,
                }}>
                  {c.workType}
                </div>
              )}

              {/* 累積勤務・乗船日 */}
              <div style={{ display: 'flex', gap: 8 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ color: '#334155', fontSize: 9, marginBottom: 2, fontWeight: 600 }}>累積勤務</div>
                  <div style={{ color: '#38BDF8', fontSize: 12, fontWeight: 700 }}>
                    {c.cumulativeHours > 0 ? `${c.cumulativeHours}h` : '—'}
                  </div>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ color: '#334155', fontSize: 9, marginBottom: 2, fontWeight: 600 }}>乗船日</div>
                  <div style={{ color: '#94A3B8', fontSize: 11 }}>
                    {c.boardedAt ? c.boardedAt.slice(0, 10) : '—'}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CrewStatusPanel;
